import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert } from 'react-native';
import { Text, TextInput, Button, Divider, ActivityIndicator, DataTable, Card, Chip, Switch } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// Denominaciones de Lempiras (Honduras)
const BILLETES = [
  { key: 'billete_500', label: 'L. 500', valor: 500 },
  { key: 'billete_200', label: 'L. 200', valor: 200 },
  { key: 'billete_100', label: 'L. 100', valor: 100 },
  { key: 'billete_50', label: 'L. 50', valor: 50 },
  { key: 'billete_20', label: 'L. 20', valor: 20 },
  { key: 'billete_10', label: 'L. 10', valor: 10 },
  { key: 'billete_5', label: 'L. 5', valor: 5 },
  { key: 'billete_2', label: 'L. 2', valor: 2 },
  { key: 'billete_1', label: 'L. 1', valor: 1 },
];

const MONEDAS = [
  { key: 'moneda_5', label: 'L. 5.00', valor: 5 },
  { key: 'moneda_2', label: 'L. 2.00', valor: 2 },
  { key: 'moneda_1', label: 'L. 1.00', valor: 1 },
  { key: 'moneda_050', label: 'L. 0.50', valor: 0.50 },
  { key: 'moneda_020', label: 'L. 0.20', valor: 0.20 },
  { key: 'moneda_010', label: 'L. 0.10', valor: 0.10 },
  { key: 'moneda_005', label: 'L. 0.05', valor: 0.05 },
];

const NuevoCierreScreen = () => {
  const navigation = useNavigation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [preview, setPreview] = useState(null);
  const [tipo, setTipo] = useState(user?.rol === 'repartidor' ? 'repartidor' : 'vendedor');
  const [observaciones, setObservaciones] = useState('');
  const [justificacion, setJustificacion] = useState('');
  const [montoEfectivoReportado, setMontoEfectivoReportado] = useState('');
  const [realizarArqueo, setRealizarArqueo] = useState(false);

  // Estado del arqueo de caja
  const [arqueo, setArqueo] = useState({
    billete_500: '', billete_200: '', billete_100: '', billete_50: '',
    billete_20: '', billete_10: '', billete_5: '', billete_2: '', billete_1: '',
    moneda_5: '', moneda_2: '', moneda_1: '',
    moneda_050: '', moneda_020: '', moneda_010: '', moneda_005: '',
    justificacion: '',
  });

  const fecha = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadPreview();
  }, [tipo]);

  const loadPreview = async () => {
    try {
      setLoadingPreview(true);
      const data = await api.getCierrePreview(tipo, fecha);
      setPreview(data);
      if (data.monto_efectivo) {
        setMontoEfectivoReportado(data.monto_efectivo.toFixed(2));
      }
    } catch (error) {
      console.error('Error al cargar preview:', error);
      Alert.alert('Error', 'No se pudo cargar la información del día');
    } finally {
      setLoadingPreview(false);
    }
  };

  const updateArqueo = (key, value) => {
    setArqueo(prev => ({ ...prev, [key]: value }));
  };

  // Calcular totales del arqueo
  const calcularTotalBilletes = () => {
    return BILLETES.reduce((sum, b) => sum + (parseInt(arqueo[b.key]) || 0) * b.valor, 0);
  };

  const calcularTotalMonedas = () => {
    return MONEDAS.reduce((sum, m) => sum + (parseInt(arqueo[m.key]) || 0) * m.valor, 0);
  };

  const totalArqueo = calcularTotalBilletes() + calcularTotalMonedas();
  const efectivoEsperado = preview ? preview.monto_efectivo : 0;
  const diferenciaArqueo = totalArqueo - efectivoEsperado;

  // Diferencia cuadre repartidor
  const montoReportado = parseFloat(montoEfectivoReportado) || 0;
  const diferenciaCuadre = tipo === 'repartidor' ? montoReportado - (preview?.monto_efectivo || 0) : 0;

  const handleSubmit = async () => {
    if (!preview) return;

    // Validaciones
    if (tipo === 'repartidor' && Math.abs(diferenciaCuadre) > 5 && !justificacion.trim()) {
      Alert.alert('Justificación Requerida', 'Debe justificar la diferencia en el cuadre del repartidor.');
      return;
    }

    if (realizarArqueo && Math.abs(diferenciaArqueo) > 5 && !arqueo.justificacion.trim()) {
      Alert.alert('Justificación Requerida', 'Debe justificar la diferencia en el arqueo de caja.');
      return;
    }

    Alert.alert(
      'Confirmar Cierre',
      `¿Desea crear el cierre ${tipo} para ${fecha}?\n\nTotal neto: L. ${(preview.total_neto || 0).toFixed(2)}`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Confirmar',
          onPress: async () => {
            try {
              setLoading(true);
              const cierreData = {
                fecha,
                tipo,
                observaciones: observaciones.trim() || null,
                justificacion_diferencia: justificacion.trim() || null,
              };

              if (tipo === 'repartidor') {
                cierreData.monto_efectivo_reportado = montoReportado;
              }

              if (realizarArqueo) {
                cierreData.arqueo = {};
                for (const b of BILLETES) {
                  cierreData.arqueo[b.key] = parseInt(arqueo[b.key]) || 0;
                }
                for (const m of MONEDAS) {
                  cierreData.arqueo[m.key] = parseInt(arqueo[m.key]) || 0;
                }
                cierreData.arqueo.justificacion = arqueo.justificacion || null;
              }

              await api.createCierre(cierreData);
              Alert.alert('Cierre Creado', 'El cierre se ha registrado exitosamente.', [
                { text: 'OK', onPress: () => navigation.goBack() }
              ]);
            } catch (error) {
              console.error('Error al crear cierre:', error);
              Alert.alert('Error', error.message || 'No se pudo crear el cierre');
            } finally {
              setLoading(false);
            }
          }
        }
      ]
    );
  };

  if (loading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loaderText}>Guardando cierre...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>Nuevo Cierre de Caja</Text>

      {/* Tipo de cierre */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Tipo de Cierre</Text>
        <View style={styles.chipRow}>
          {user?.rol !== 'repartidor' && (
            <Chip
              onPress={() => setTipo('vendedor')}
              style={[styles.tipoChip, tipo === 'vendedor' && styles.tipoChipActive]}
              textStyle={tipo === 'vendedor' ? styles.tipoChipTextActive : styles.tipoChipTextInactive}
              icon="cart"
            >
              Vendedor
            </Chip>
          )}
          {(user?.rol === 'repartidor' || user?.rol === 'admin') && (
            <Chip
              onPress={() => setTipo('repartidor')}
              style={[styles.tipoChip, tipo === 'repartidor' && styles.tipoChipActive]}
              textStyle={tipo === 'repartidor' ? styles.tipoChipTextActive : styles.tipoChipTextInactive}
              icon="car"
            >
              Repartidor
            </Chip>
          )}
          {['admin', 'supervisor', 'gerente'].includes(user?.rol) && (
            <Chip
              onPress={() => setTipo('consolidado')}
              style={[styles.tipoChip, tipo === 'consolidado' && styles.tipoChipActive]}
              textStyle={tipo === 'consolidado' ? styles.tipoChipTextActive : styles.tipoChipTextInactive}
              icon="analytics"
            >
              Consolidado
            </Chip>
          )}
        </View>
      </View>

      {/* Info general */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Información General</Text>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Fecha:</Text>
          <Text style={styles.infoValue}>{fecha}</Text>
        </View>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Usuario:</Text>
          <Text style={styles.infoValue}>{user?.nombre || user?.email}</Text>
        </View>
      </View>

      <Divider style={styles.divider} />

      {/* Resumen automático */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          <Ionicons name="calculator" size={16} color="#0066cc" /> Resumen Automático del Día
        </Text>

        {loadingPreview ? (
          <ActivityIndicator size="small" color="#0066cc" />
        ) : preview ? (
          <>
            <View style={styles.resumenGrid}>
              <View style={styles.resumenItem}>
                <Text style={styles.resumenLabel}>Vendido (bruto)</Text>
                <Text style={styles.resumenValue}>L. {(preview.total_ventas || 0).toFixed(2)}</Text>
                <Text style={styles.resumenCount}>{preview.cantidad_ventas || 0} ventas (incluye crédito)</Text>
              </View>
              <View style={styles.resumenItem}>
                <Text style={styles.resumenLabel}>A crédito</Text>
                <Text style={[styles.resumenValue, { color: '#ff9800' }]}>L. {(preview.monto_credito || 0).toFixed(2)}</Text>
                <Text style={styles.resumenCount}>queda en cuentas por cobrar</Text>
              </View>
              <View style={styles.resumenItem}>
                <Text style={styles.resumenLabel}>Abonos CxC</Text>
                <Text style={[styles.resumenValue, { color: '#2196f3' }]}>L. {(preview.total_abonos_cxc || 0).toFixed(2)}</Text>
                <Text style={styles.resumenCount}>{preview.cantidad_abonos_cxc || 0} abonos</Text>
              </View>
              <View style={styles.resumenItem}>
                <Text style={styles.resumenLabel}>Devoluciones</Text>
                <Text style={[styles.resumenValue, { color: '#f44336' }]}>-L. {(preview.total_devoluciones || 0).toFixed(2)}</Text>
                <Text style={styles.resumenCount}>{preview.cantidad_devoluciones || 0}</Text>
              </View>
              <View style={[styles.resumenItem, styles.resumenNeto]}>
                <Text style={styles.resumenLabel}>Recaudado (entró a caja)</Text>
                <Text style={[styles.resumenValue, { color: '#4caf50', fontSize: 20 }]}>L. {(preview.total_recaudado || 0).toFixed(2)}</Text>
                <Text style={styles.resumenCount}>contado + abonos a CxC</Text>
              </View>
            </View>

            {/* Desglose por método */}
            <Card style={styles.metodoCard}>
              <Card.Content>
                <Text style={styles.metodoTitle}>Desglose del día por Método de Pago</Text>
                <Text style={styles.metodoSubtitle}>Cómo se distribuyó lo vendido. Suman al "Vendido (bruto)", no son adicionales.</Text>
                <View style={styles.metodoRow}><Text>Efectivo</Text><Text style={styles.metodoMonto}>L. {(preview.monto_efectivo || 0).toFixed(2)}</Text></View>
                <View style={styles.metodoRow}><Text>Tarjeta</Text><Text style={styles.metodoMonto}>L. {(preview.monto_tarjeta || 0).toFixed(2)}</Text></View>
                <View style={styles.metodoRow}><Text>Transferencia</Text><Text style={styles.metodoMonto}>L. {(preview.monto_transferencia || 0).toFixed(2)}</Text></View>
                <View style={styles.metodoRow}>
                  <Text>Crédito <Text style={{ fontSize: 11, color: '#888' }}>(no entró a caja)</Text></Text>
                  <Text style={[styles.metodoMonto, { color: '#ff9800' }]}>L. {(preview.monto_credito || 0).toFixed(2)}</Text>
                </View>
              </Card.Content>
            </Card>

            {/* Ventas del día */}
            {preview.ventas && preview.ventas.length > 0 && (
              <>
                <Text style={styles.ventasTitle}>Ventas del Día ({preview.ventas.length})</Text>
                <DataTable style={styles.dataTable}>
                  <DataTable.Header>
                    <DataTable.Title>ID</DataTable.Title>
                    <DataTable.Title>Cliente</DataTable.Title>
                    <DataTable.Title>Estado</DataTable.Title>
                    <DataTable.Title numeric>Total</DataTable.Title>
                  </DataTable.Header>
                  {preview.ventas.slice(0, 20).map(v => (
                    <DataTable.Row key={v.id}>
                      <DataTable.Cell>#{v.id}</DataTable.Cell>
                      <DataTable.Cell>{(v.cliente_nombre || '').substring(0, 15)}</DataTable.Cell>
                      <DataTable.Cell>
                        <Chip mode="outlined" style={{ height: 24, backgroundColor: v.estado === 'completada' ? '#ccffcc' : v.estado === 'entregado' ? '#e3f2fd' : '#fff0cc' }} textStyle={{ fontSize: 10 }}>
                          {v.estado}
                        </Chip>
                      </DataTable.Cell>
                      <DataTable.Cell numeric>L. {(v.total || 0).toFixed(2)}</DataTable.Cell>
                    </DataTable.Row>
                  ))}
                </DataTable>
                {preview.ventas.length > 20 && (
                  <Text style={styles.moreText}>... y {preview.ventas.length - 20} ventas más</Text>
                )}
              </>
            )}
          </>
        ) : (
          <View style={styles.noDataBox}>
            <Ionicons name="information-circle-outline" size={28} color="#888" />
            <Text style={styles.noDataTitle}>Sin movimientos para este cierre</Text>
            <Text style={styles.noDataDetail}>
              {tipo === 'vendedor'
                ? 'No registraste ventas hoy con tu usuario.'
                : tipo === 'repartidor'
                  ? 'No tienes pedidos asignados o entregados hoy.'
                  : 'No hay ventas registradas hoy en la empresa.'}
              {'\n'}Aún podés guardar un cierre en cero (útil para auditoría) o cambiar el tipo arriba.
            </Text>
          </View>
        )}
      </View>

      <Divider style={styles.divider} />

      {/* Cuadre del repartidor (9.1) */}
      {tipo === 'repartidor' && preview && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            <Ionicons name="swap-horizontal" size={16} color="#ff9800" /> Cuadre del Repartidor
          </Text>

          <Card style={[styles.cuadreCard, Math.abs(diferenciaCuadre) > 5 ? styles.cuadreError : styles.cuadreOk]}>
            <Card.Content>
              <View style={styles.cuadreRow}>
                <Text style={styles.cuadreLabel}>Entregado (sistema):</Text>
                <Text style={styles.cuadreValue}>L. {(preview.monto_efectivo || 0).toFixed(2)}</Text>
              </View>

              <TextInput
                label="Efectivo recaudado (reportado)"
                value={montoEfectivoReportado}
                onChangeText={setMontoEfectivoReportado}
                keyboardType="numeric"
                style={styles.input}
                left={<TextInput.Affix text="L." />}
              />

              <View style={styles.cuadreRow}>
                <Text style={styles.cuadreLabel}>Diferencia:</Text>
                <Text style={[styles.cuadreValue, { color: Math.abs(diferenciaCuadre) > 5 ? '#f44336' : '#4caf50', fontSize: 18 }]}>
                  {diferenciaCuadre >= 0 ? '+' : ''}L. {diferenciaCuadre.toFixed(2)}
                </Text>
              </View>

              {Math.abs(diferenciaCuadre) > 5 && (
                <>
                  <Text style={styles.warningText}>
                    <Ionicons name="warning" size={14} color="#f44336" /> Diferencia mayor a L. 5.00 - Justificación obligatoria
                  </Text>
                  <TextInput
                    label="Justificación de la diferencia *"
                    value={justificacion}
                    onChangeText={setJustificacion}
                    style={styles.input}
                    multiline
                    numberOfLines={3}
                  />
                </>
              )}
            </Card.Content>
          </Card>
        </View>
      )}

      <Divider style={styles.divider} />

      {/* Arqueo de Caja con denominaciones hondureñas */}
      <View style={styles.section}>
        <View style={styles.arqueoHeader}>
          <Text style={styles.sectionTitle}>
            <Ionicons name="cash" size={16} color="#6a1b9a" /> Arqueo de Caja (Lempiras)
          </Text>
          <Switch value={realizarArqueo} onValueChange={setRealizarArqueo} color="#6a1b9a" />
        </View>

        {realizarArqueo && (
          <Card style={styles.arqueoCard}>
            <Card.Content>
              {/* Billetes */}
              <Text style={styles.denominacionTitle}>
                <Ionicons name="document" size={14} color="#2e7d32" /> Billetes
              </Text>
              {BILLETES.map(b => (
                <View key={b.key} style={styles.denominacionRow}>
                  <Text style={styles.denominacionLabel}>{b.label}</Text>
                  <TextInput
                    value={arqueo[b.key]}
                    onChangeText={(v) => updateArqueo(b.key, v)}
                    keyboardType="numeric"
                    style={styles.denominacionInput}
                    dense
                    placeholder="0"
                  />
                  <Text style={styles.denominacionTotal}>
                    L. {((parseInt(arqueo[b.key]) || 0) * b.valor).toFixed(2)}
                  </Text>
                </View>
              ))}

              <View style={styles.subtotalRow}>
                <Text style={styles.subtotalLabel}>Subtotal Billetes:</Text>
                <Text style={styles.subtotalValue}>L. {calcularTotalBilletes().toFixed(2)}</Text>
              </View>

              <Divider style={styles.dividerSmall} />

              {/* Monedas */}
              <Text style={styles.denominacionTitle}>
                <Ionicons name="ellipse" size={14} color="#e65100" /> Monedas
              </Text>
              {MONEDAS.map(m => (
                <View key={m.key} style={styles.denominacionRow}>
                  <Text style={styles.denominacionLabel}>{m.label}</Text>
                  <TextInput
                    value={arqueo[m.key]}
                    onChangeText={(v) => updateArqueo(m.key, v)}
                    keyboardType="numeric"
                    style={styles.denominacionInput}
                    dense
                    placeholder="0"
                  />
                  <Text style={styles.denominacionTotal}>
                    L. {((parseInt(arqueo[m.key]) || 0) * m.valor).toFixed(2)}
                  </Text>
                </View>
              ))}

              <View style={styles.subtotalRow}>
                <Text style={styles.subtotalLabel}>Subtotal Monedas:</Text>
                <Text style={styles.subtotalValue}>L. {calcularTotalMonedas().toFixed(2)}</Text>
              </View>

              <Divider style={styles.dividerSmall} />

              {/* Totales del arqueo */}
              <View style={styles.arqueoTotalSection}>
                <View style={styles.arqueoTotalRow}>
                  <Text style={styles.arqueoTotalLabel}>Total Arqueo:</Text>
                  <Text style={styles.arqueoTotalValue}>L. {totalArqueo.toFixed(2)}</Text>
                </View>
                <View style={styles.arqueoTotalRow}>
                  <Text style={styles.arqueoTotalLabel}>Efectivo esperado:</Text>
                  <Text style={styles.arqueoEsperado}>L. {efectivoEsperado.toFixed(2)}</Text>
                </View>
                <View style={[styles.arqueoTotalRow, styles.diferenciaBg, { backgroundColor: Math.abs(diferenciaArqueo) <= 5 ? '#e8f5e9' : '#ffebee' }]}>
                  <Text style={styles.arqueoTotalLabel}>Diferencia:</Text>
                  <Text style={[styles.diferenciaValue, { color: Math.abs(diferenciaArqueo) <= 5 ? '#2e7d32' : '#c62828' }]}>
                    {diferenciaArqueo >= 0 ? '+' : ''}L. {diferenciaArqueo.toFixed(2)}
                  </Text>
                </View>
              </View>

              {Math.abs(diferenciaArqueo) > 5 && (
                <>
                  <Text style={styles.warningText}>
                    <Ionicons name="warning" size={14} color="#f44336" /> Diferencia mayor a L. 5.00 - Justifique
                  </Text>
                  <TextInput
                    label="Justificación del arqueo *"
                    value={arqueo.justificacion}
                    onChangeText={(v) => updateArqueo('justificacion', v)}
                    style={styles.input}
                    multiline
                    numberOfLines={2}
                  />
                </>
              )}
            </Card.Content>
          </Card>
        )}
      </View>

      <Divider style={styles.divider} />

      {/* Observaciones */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Observaciones</Text>
        <TextInput
          label="Observaciones (opcional)"
          value={observaciones}
          onChangeText={setObservaciones}
          style={styles.input}
          multiline
          numberOfLines={3}
        />
      </View>

      {/* Botones */}
      <View style={styles.buttonContainer}>
        <Button mode="contained" onPress={handleSubmit} style={styles.submitButton} icon="check" disabled={!preview || loadingPreview}>
          Guardar Cierre
        </Button>
        <Button mode="outlined" onPress={() => navigation.goBack()} style={styles.cancelButton}>
          Cancelar
        </Button>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 16 },
  title: { fontSize: 22, fontWeight: 'bold', marginBottom: 16, textAlign: 'center' },
  section: { marginBottom: 16 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', marginBottom: 10, color: '#0066cc' },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap' },
  tipoChip: { marginRight: 8, marginBottom: 4, backgroundColor: '#f0f0f0' },
  tipoChipActive: { backgroundColor: '#4CAF50' },
  tipoChipTextInactive: { color: '#333' },
  tipoChipTextActive: { color: 'white', fontWeight: '600' },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
  infoLabel: { fontWeight: 'bold', color: '#555' },
  infoValue: { color: '#333' },
  divider: { marginVertical: 12 },
  dividerSmall: { marginVertical: 8 },
  resumenGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  resumenItem: { width: '48%', backgroundColor: '#fff', padding: 12, borderRadius: 8, marginBottom: 8, elevation: 1, alignItems: 'center' },
  resumenNeto: { width: '100%', backgroundColor: '#f1f8e9' },
  resumenLabel: { fontSize: 12, color: '#666', marginBottom: 4 },
  resumenValue: { fontSize: 16, fontWeight: 'bold' },
  resumenCount: { fontSize: 11, color: '#999', marginTop: 2 },
  metodoCard: { marginTop: 10, elevation: 1 },
  metodoTitle: { fontSize: 14, fontWeight: 'bold', marginBottom: 4, color: '#555' },
  metodoSubtitle: { fontSize: 11, color: '#888', marginBottom: 8, fontStyle: 'italic' },
  metodoRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
  metodoMonto: { fontWeight: 'bold' },
  ventasTitle: { fontSize: 14, fontWeight: 'bold', marginTop: 12, marginBottom: 6 },
  dataTable: { backgroundColor: '#fff', borderRadius: 4 },
  moreText: { fontSize: 12, color: '#999', textAlign: 'center', marginTop: 4 },
  noData: { textAlign: 'center', color: '#999', marginTop: 20 },
  noDataBox: { alignItems: 'center', padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8, marginTop: 8 },
  noDataTitle: { fontSize: 14, fontWeight: '600', color: '#666', marginTop: 8 },
  noDataDetail: { fontSize: 12, color: '#888', textAlign: 'center', marginTop: 6, lineHeight: 16 },
  input: { marginBottom: 8, backgroundColor: 'white' },
  // Cuadre repartidor
  cuadreCard: { marginTop: 8, elevation: 2 },
  cuadreOk: { borderLeftWidth: 4, borderLeftColor: '#4caf50' },
  cuadreError: { borderLeftWidth: 4, borderLeftColor: '#f44336' },
  cuadreRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6 },
  cuadreLabel: { fontSize: 14, color: '#555' },
  cuadreValue: { fontSize: 16, fontWeight: 'bold' },
  warningText: { color: '#f44336', fontSize: 12, marginBottom: 8, marginTop: 4 },
  // Arqueo de caja
  arqueoHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  arqueoCard: { marginTop: 8, elevation: 2, borderLeftWidth: 4, borderLeftColor: '#6a1b9a' },
  denominacionTitle: { fontSize: 14, fontWeight: 'bold', color: '#333', marginBottom: 8, marginTop: 4 },
  denominacionRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4, paddingHorizontal: 4 },
  denominacionLabel: { width: 70, fontSize: 14, fontWeight: '600' },
  denominacionInput: { width: 80, backgroundColor: '#fff', marginHorizontal: 8, height: 36, fontSize: 14 },
  denominacionTotal: { flex: 1, textAlign: 'right', fontSize: 14, fontWeight: '500', color: '#333' },
  subtotalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, paddingHorizontal: 4, backgroundColor: '#f5f5f5', borderRadius: 4, marginTop: 4 },
  subtotalLabel: { fontWeight: 'bold', color: '#555' },
  subtotalValue: { fontWeight: 'bold', color: '#333', fontSize: 15 },
  arqueoTotalSection: { marginTop: 8 },
  arqueoTotalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, paddingHorizontal: 4 },
  arqueoTotalLabel: { fontSize: 15, fontWeight: 'bold', color: '#333' },
  arqueoTotalValue: { fontSize: 18, fontWeight: 'bold', color: '#6a1b9a' },
  arqueoEsperado: { fontSize: 15, fontWeight: '600', color: '#555' },
  diferenciaBg: { borderRadius: 6, padding: 8, marginTop: 4 },
  diferenciaValue: { fontSize: 18, fontWeight: 'bold' },
  // Botones
  buttonContainer: { marginVertical: 20 },
  submitButton: { marginBottom: 10, paddingVertical: 4, backgroundColor: '#0066cc' },
  cancelButton: { paddingVertical: 4 },
  loaderContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 50 },
  loaderText: { marginTop: 10, color: '#666' },
});

export default NuevoCierreScreen;
